<template>
  <div id="AdminTransactionsList">
    <div class="container-fluid mt-5 mb-5">
      <div class="d-flex align-items-center gap-2 mb-3">
        <h2 class="mb-0">{{ view.heading }} transactions</h2>
        <TransactionNotificationSettingsButton class="me-auto" />
        <router-link
          v-if="!isForwardsView"
          to="/admin/transactions/new"
          class="btn btn-primary"
        >
          <font-awesome-icon icon="plus" /> New Transaction
        </router-link>
        <!-- Forwards: the deals overview, and the future-inventory picker the new-transaction page
             also offers, opened directly -->
        <template v-else>
          <router-link
            :to="{ name: 'ForwardsOverview' }"
            class="btn btn-outline-primary"
          >
            <font-awesome-icon icon="handshake" /> Forward deals
          </router-link>
          <router-link
            :to="{ name: 'AdminTransactionsNewForwardView' }"
            class="btn btn-primary"
          >
            <font-awesome-icon icon="plus" /> New forward
          </router-link>
        </template>
      </div>

      <SegmentedControl
        v-model="activeView"
        :options="viewOptions"
        fit
        class="mb-4"
        data-testid="transactions-view-toggle"
      />

      <LoadingIndicatorComponent v-if="isLoading" />

      <div
        v-else-if="visibleTransactions.length === 0"
        class="alert alert-info"
      >
        No {{ view.noun }} transactions found.
      </div>

      <template v-else>
        <!-- Pending / In-Progress Transactions -->
        <div>
          <h4 class="font-serif mb-3">
            Pending
            <span class="badge bg-secondary ms-2">{{
              pendingTransactions.length
            }}</span>
          </h4>
          <!-- Keyed on the view: spot and forward rows have different counterparties and columns,
               so a filter typed on one view must not keep filtering the other -->
          <SalesTransactionsTable
            :key="`pending-${activeView}`"
            :transactions="pendingTransactions"
            :showActions="true"
            :showBuyerInvoiceStatus="true"
            :showDeal="isForwardsView"
            :emptyMessage="`No pending ${view.noun} transactions.`"
          >
            <template #actions="{ transaction }">
              <div class="dropdown">
                <button
                  class="btn btn-sm btn-outline-primary dropdown-toggle position-relative"
                  type="button"
                  data-bs-toggle="dropdown"
                  data-bs-config='{"popperConfig":{"strategy":"fixed"}}'
                  aria-expanded="false"
                  :disabled="isActionInProgress(transaction.id)"
                >
                  <span
                    v-if="isActionInProgress(transaction.id)"
                    class="spinner-border spinner-border-sm me-1"
                  ></span>
                  Actions
                  <span
                    v-if="needsMeritRegistration(transaction)"
                    class="merit-todo-dot"
                    title="Invoice not registered with Merit Aktiva"
                  ></span>
                </button>
                <ul class="dropdown-menu dropdown-menu-end">
                  <li>
                    <router-link
                      :to="`/admin/transactions/${transaction.id}/edit`"
                      class="dropdown-item"
                    >
                      <font-awesome-icon icon="edit" class="me-1" /> Edit
                    </router-link>
                  </li>
                  <li v-if="transaction.status === 'READY_TO_TRANSFER'">
                    <button
                      class="dropdown-item"
                      type="button"
                      @click="openTransferModal(transaction.id)"
                    >
                      <font-awesome-icon icon="exchange-alt" class="me-1" />
                      Transfer
                    </button>
                  </li>
                  <li v-if="transaction.status === 'READY_TO_TRANSFER'">
                    <button
                      class="dropdown-item"
                      type="button"
                      @click="openDeliverModal(transaction)"
                    >
                      <font-awesome-icon icon="share-square" class="me-1" />
                      Deliver to Hosted Account
                    </button>
                  </li>
                  <li
                    v-if="
                      transaction.status === 'READY_TO_TRANSFER' ||
                      transaction.status === 'DELIVERED'
                    "
                  >
                    <button
                      class="dropdown-item"
                      type="button"
                      @click="openCreateInvoiceModal(transaction)"
                    >
                      <font-awesome-icon icon="file-invoice" class="me-1" />
                      Create Invoice
                    </button>
                  </li>
                  <li v-if="transactionInvoiceMap[transaction.id]">
                    <button
                      class="dropdown-item"
                      type="button"
                      @click="
                        downloadInvoice(transactionInvoiceMap[transaction.id])
                      "
                    >
                      <font-awesome-icon icon="download" class="me-1" />
                      Download Invoice
                    </button>
                  </li>
                  <li>
                    <button
                      class="dropdown-item"
                      type="button"
                      @click="downloadFeeCsv(transaction.id)"
                    >
                      <font-awesome-icon icon="file-export" class="me-1" />
                      Download Fee CSV
                    </button>
                  </li>
                  <li
                    v-if="canSendBuyerInvoice(transaction)"
                    :title="
                      transaction.counterparty_emails
                        ? undefined
                        : 'No buyer email addresses on this transaction — add counterparty emails first'
                    "
                  >
                    <button
                      class="dropdown-item"
                      type="button"
                      :disabled="!transaction.counterparty_emails"
                      @click="handleSendInvoice(transaction)"
                    >
                      <font-awesome-icon icon="envelope" class="me-1" />
                      Send Invoice
                    </button>
                  </li>
                  <li v-if="canSendBuyerInvoice(transaction)">
                    <button
                      class="dropdown-item"
                      type="button"
                      title="Record that the invoice was sent to the buyer outside the platform"
                      @click="openMarkSentModal(transaction)"
                    >
                      <font-awesome-icon icon="calendar-check" class="me-1" />
                      Mark Invoice as Sent
                    </button>
                  </li>
                  <li v-if="needsMeritRegistration(transaction)">
                    <button
                      class="dropdown-item"
                      type="button"
                      title="Register buyer sales invoice with Merit Aktiva without emailing the buyer"
                      @click="handleRegisterWithMerit(transaction)"
                    >
                      <font-awesome-icon icon="file-invoice" class="me-1" />
                      Register with Merit
                    </button>
                  </li>
                  <li v-if="transaction.status === 'DELIVERED'">
                    <button
                      class="dropdown-item"
                      type="button"
                      @click="
                        openConfirmationModal(
                          'Complete Transaction',
                          `Are you sure you want to mark ${transaction.amount} MWh transaction with ${transaction.counterparty_name} as completed and funds received?`,
                          () => completeTransaction(transaction.id)
                        )
                      "
                    >
                      <font-awesome-icon icon="check" class="me-1" /> Received
                      funds
                    </button>
                  </li>
                </ul>
              </div>
            </template>
          </SalesTransactionsTable>
        </div>

        <!-- Completed Transactions -->
        <div class="mt-5">
          <div class="d-flex justify-content-between align-items-center mb-3">
            <h4 class="font-serif mb-0">
              Completed
              <span class="badge bg-success ms-2">{{
                completedTransactions.length
              }}</span>
            </h4>
            <ActionButtonWithSpinner
              variant="btn-outline-primary"
              icon="sync"
              :busy="isReconcilingBuyerInvoices"
              @click="reconcileBuyerInvoicePaidStatus"
            >
              Reconcile buyer invoice paid status
            </ActionButtonWithSpinner>
          </div>
          <SalesTransactionsTable
            :key="`completed-${activeView}`"
            :transactions="completedTransactions"
            :showActions="true"
            :showBuyerInvoiceStatus="true"
            :showDeal="isForwardsView"
            :emptyMessage="`No completed ${view.noun} transactions.`"
          >
            <template #actions="{ transaction }">
              <div class="dropdown">
                <button
                  class="btn btn-sm btn-outline-primary dropdown-toggle position-relative"
                  type="button"
                  data-bs-toggle="dropdown"
                  data-bs-config='{"popperConfig":{"strategy":"fixed"}}'
                  aria-expanded="false"
                  :disabled="isCompletedActionInProgress(transaction.id)"
                >
                  <span
                    v-if="isCompletedActionInProgress(transaction.id)"
                    class="spinner-border spinner-border-sm me-1"
                  ></span>
                  Actions
                  <span
                    v-if="needsMeritRegistration(transaction)"
                    class="merit-todo-dot"
                    title="Invoice not registered with Merit Aktiva"
                  ></span>
                </button>
                <ul class="dropdown-menu dropdown-menu-end">
                  <li v-if="transactionInvoiceMap[transaction.id]">
                    <button
                      class="dropdown-item"
                      type="button"
                      title="Download buyer sales invoice"
                      @click="
                        downloadInvoice(transactionInvoiceMap[transaction.id])
                      "
                    >
                      <font-awesome-icon icon="download" class="me-1" />
                      Download Invoice
                    </button>
                  </li>
                  <li>
                    <button
                      class="dropdown-item"
                      type="button"
                      title="Download CSV with fee invoice data"
                      @click="downloadFeeCsv(transaction.id)"
                    >
                      <font-awesome-icon icon="file-export" class="me-1" />
                      Download Fee CSV
                    </button>
                  </li>
                  <li v-if="transactionInvoiceMap[transaction.id]">
                    <button
                      class="dropdown-item"
                      type="button"
                      :disabled="
                        transaction.buyer_sales_invoice_merit_registered
                      "
                      :title="
                        transaction.buyer_sales_invoice_merit_registered
                          ? 'Invoice is already registered in Merit Aktiva'
                          : 'Register buyer sales invoice with Merit Aktiva without emailing the buyer'
                      "
                      @click="handleRegisterWithMerit(transaction)"
                    >
                      <font-awesome-icon icon="file-invoice" class="me-1" />
                      Register with Merit
                    </button>
                  </li>
                </ul>
              </div>
            </template>
          </SalesTransactionsTable>
        </div>
      </template>
    </div>

    <!-- Transfer Modal -->
    <TransferModalComponent
      :visible="showTransferModal"
      :transactionId="selectedTransactionId"
      @confirm="handleTransferConfirm"
      @cancel="closeTransferModal"
      ref="transferModalRef"
    />

    <!-- Confirmation Modal for Complete Action -->
    <ConfirmationModalComponent
      :visible="showConfirmationModal"
      :title="confirmationModalTitle"
      :message="confirmationModalMessage"
      @confirm="handleConfirm"
      @cancel="closeConfirmationModal"
    />

    <!-- Create Buyer Sales Invoice Modal -->
    <CreateBuyerSalesInvoiceModal
      :show="showCreateInvoiceModal"
      :company-id="SOLDERA_COMPANY_ID"
      :sales-transaction-id="selectedTransactionForInvoice?.id"
      :pre-fill-description="invoiceModalPrefill.preFillDescription"
      :pre-fill-amount="invoiceModalPrefill.preFillAmount"
      :pre-fill-unit-price="invoiceModalPrefill.preFillUnitPrice"
      :pre-fill-buyer-name="invoiceModalPrefill.preFillBuyerName"
      :pre-fill-buyer-address="invoiceModalPrefill.preFillBuyerAddress"
      :pre-fill-buyer-country="invoiceModalPrefill.preFillBuyerCountry"
      :pre-fill-buyer-registry-code="
        invoiceModalPrefill.preFillBuyerRegistryCode
      "
      :pre-fill-buyer-vat="invoiceModalPrefill.preFillBuyerVat"
      :pre-fill-buyer-emails="invoiceModalPrefill.preFillBuyerEmails"
      @close="handleModalClose"
      @invoice-created="handleInvoiceCreated"
    />

    <!-- Mark Buyer Invoice as Sent Manually Modal -->
    <ModalComponent
      submitButtonText="Mark as Sent"
      :hideSubmitButton="true"
      @setForceToggle="setMarkSentForceToggle"
      @close="closeMarkSentModal"
    >
      <template #title>Mark Invoice as Sent</template>
      <template #body>
        <p v-if="selectedTransactionForMarkSent" class="mb-3">
          Record that the buyer invoice for
          <strong>{{
            selectedTransactionForMarkSent.counterparty_name
          }}</strong>
          was sent outside the platform. No email will be sent.
        </p>
        <label for="markSentDate" class="form-label">Date sent</label>
        <input
          id="markSentDate"
          v-model="markSentDate"
          type="date"
          class="form-control"
          :max="todayIsoDate"
        />
      </template>
      <template #footer>
        <ActionButtonWithSpinner
          variant="btn-primary"
          size="md"
          :busy="isMarkingSent"
          :disabled="!markSentDate"
          @click="confirmMarkSent"
        >
          Mark as Sent
        </ActionButtonWithSpinner>
      </template>
    </ModalComponent>

    <!-- Deliver to Virtual Account Modal -->
    <ModalComponent
      submitButtonText="Deliver"
      :hideSubmitButton="true"
      @setForceToggle="setDeliverForceToggle"
      @close="closeDeliverModal"
    >
      <template #title>Deliver to Hosted Account</template>
      <template #body>
        <div v-if="selectedTransactionForDeliver" class="mb-3">
          <p class="mb-1">
            <strong>Amount:</strong>
            {{ selectedTransactionForDeliver.amount }} MWh
          </p>
          <p class="mb-1">
            <strong>Counterparty:</strong>
            {{ selectedTransactionForDeliver.counterparty_name }}
          </p>
        </div>
        <CompanySearchDropdown
          v-model="deliverTargetCompanyId"
          :withLabel="true"
          :withDescription="false"
          placeholder="Select target company..."
        />
      </template>
      <template #footer>
        <ActionButtonWithSpinner
          variant="btn-primary"
          size="md"
          :busy="isDelivering"
          :disabled="!deliverTargetCompanyId"
          @click="confirmDeliver"
        >
          Deliver
        </ActionButtonWithSpinner>
      </template>
    </ModalComponent>
  </div>
</template>

<script setup lang="ts">
import ActionButtonWithSpinner from "@/components/common/ActionButtonWithSpinner.vue";
import CompanySearchDropdown from "@/components/common/CompanySearchDropdown.vue";
import ConfirmationModalComponent from "@/components/common/ConfirmationModalComponent.vue";
import LoadingIndicatorComponent from "@/components/common/LoadingIndicatorComponent.vue";
import ModalComponent from "@/components/common/ModalComponent.vue";
import SegmentedControl, {
  type SegmentedControlOption,
} from "@/components/common/SegmentedControl.vue";
import { formatDateTimeIntoIsoDate } from "@/composables/formatUtils";
import { useBusy, useRowAction } from "@/composables/useBusyAction";
import { useFileDownload } from "@/composables/useFileDownload";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";
import { goSolidApi } from "@/composables/useGoSolidApi";
import { useQueryManager } from "@/composables/useQueryManager";
import CreateBuyerSalesInvoiceModal from "@/money/invoice/CreateBuyerSalesInvoiceModal.vue";
import { SOLDERA_COMPANY_ID } from "@/money/money_constants";
import { SalesTransaction } from "@/types";
import { downloadFromApiResponse } from "@/utils/downloadUtils";
import { partition } from "lodash-es";
import { computed, onMounted, ref, useTemplateRef } from "vue";
import TransactionNotificationSettingsButton from "./components/TransactionNotificationSettingsButton.vue";
import SalesTransactionsTable from "./SalesTransactionsTable.vue";
import TransferModalComponent from "./TransferModalComponent.vue";
import {
  buildInvoiceModalPrefill,
  canSendBuyerInvoice,
  DEFAULT_TRANSACTIONS_VIEW,
  isForwardTransaction,
  needsMeritRegistration,
  TRANSACTIONS_VIEW_COPY,
  TRANSACTIONS_VIEW_QUERY_KEY,
  TRANSACTIONS_VIEWS,
  type TransactionsView,
} from "./transactionsListViewHelpers";

const { globalErrorHandler, globalSuccessHandler } = useGlobalHandlers();
const { downloadInvoice } = useFileDownload();

const transactions = ref<SalesTransaction[]>([]);
const isLoading = ref(true);

// Reconcile is a single (non row-scoped) action; useBusy wraps the
// set-before / clear-in-finally flag around the request.
const { busy: isReconcilingBuyerInvoices, run: runReconcile } = useBusy();

// Spot sales and forward deliveries are worked separately, so the page shows one kind at a time.
// The choice lives in `?view=` so a reload or a shared link keeps it.
const { getQueryValueWithDefault, parsers } = useQueryManager();
const activeView = getQueryValueWithDefault(
  TRANSACTIONS_VIEW_QUERY_KEY,
  parsers.enum(TRANSACTIONS_VIEWS),
  DEFAULT_TRANSACTIONS_VIEW
);
const view = computed(() => TRANSACTIONS_VIEW_COPY[activeView.value]);
const isForwardsView = computed(() => activeView.value === "forwards");

const transactionsByView = computed<
  Record<TransactionsView, SalesTransaction[]>
>(() => {
  const [forwards, spot] = partition(transactions.value, isForwardTransaction);
  return { spot, forwards };
});

const isPending = (t: SalesTransaction) => t.status !== "COMPLETED";

const visibleTransactions = computed(
  () => transactionsByView.value[activeView.value]
);
const pendingTransactions = computed(() =>
  visibleTransactions.value.filter(isPending)
);
const completedTransactions = computed(() =>
  visibleTransactions.value.filter((t) => !isPending(t))
);

// The pills count each view's open work, so the other view's backlog stays visible.
const viewOptions = computed<SegmentedControlOption<TransactionsView>[]>(() =>
  TRANSACTIONS_VIEWS.map((v) => ({
    value: v,
    label: TRANSACTIONS_VIEW_COPY[v].label,
    count: transactionsByView.value[v].filter(isPending).length,
    countMuted: true,
  }))
);

// Transfer modal state
const showTransferModal = ref(false);
const selectedTransactionId = ref<number>(0);
const transferringTransactionId = ref<number | null>(null);
const transferModalRef =
  useTemplateRef<InstanceType<typeof TransferModalComponent>>(
    "transferModalRef"
  );

// Confirmation modal state (for complete action)
const showConfirmationModal = ref(false);
const confirmationModalTitle = ref("");
const confirmationModalMessage = ref("");
let confirmAction: (() => void) | null = null;

// Per-row busy state for the complete ("Received funds") action; useRowAction
// wraps the set-before / clear-in-finally flow around the request.
const completing = useRowAction();

// Create invoice modal state
const showCreateInvoiceModal = ref(false);
const selectedTransactionForInvoice = ref<SalesTransaction | null>(null);
const creatingInvoiceForTransactionId = ref<number | null>(null);
const invoiceModalPrefill = computed(() =>
  buildInvoiceModalPrefill(selectedTransactionForInvoice.value)
);

// Invoice tracking - maps transaction ID to most recent invoice ID
const transactionInvoiceMap = ref<Record<number, number>>({});

// Per-row busy state for the send-invoice action.
const sendingInvoice = useRowAction();

// Per-row busy state for the register-with-Merit action.
const registeringWithMerit = useRowAction();

const isActionInProgress = (transactionId: number): boolean =>
  transferringTransactionId.value === transactionId ||
  creatingInvoiceForTransactionId.value === transactionId ||
  sendingInvoice.isBusy(transactionId) ||
  registeringWithMerit.isBusy(transactionId) ||
  completing.isBusy(transactionId);

const isCompletedActionInProgress = (transactionId: number): boolean =>
  registeringWithMerit.isBusy(transactionId) ||
  downloadingFeeCsvIds.value.has(transactionId);

// Deliver to virtual account modal state
const deliverForceToggle = ref<((value: boolean) => void) | null>(null);
const selectedTransactionForDeliver = ref<SalesTransaction | null>(null);
const deliverTargetCompanyId = ref<number | null>(null);
const { busy: isDelivering, run: runDeliver } = useBusy();

const setDeliverForceToggle = (fn: (value: boolean) => void) => {
  deliverForceToggle.value = fn;
};

// Mark buyer invoice as sent manually
const markSentForceToggle = ref<((value: boolean) => void) | null>(null);
const selectedTransactionForMarkSent = ref<SalesTransaction | null>(null);
const markSentDate = ref<string>("");
const { busy: isMarkingSent, run: runMarkSent } = useBusy();
// Local calendar day (not UTC) so admins east of UTC don't default to yesterday; refreshed on
// each modal open rather than cached, so it stays right across midnight.
const todayIsoDate = ref("");

const setMarkSentForceToggle = (fn: (value: boolean) => void) => {
  markSentForceToggle.value = fn;
};

// Per-transaction fee CSV export (fee invoice breakdown per company)
const downloadingFeeCsvIds = ref<Set<number>>(new Set());

const downloadFeeCsv = async (transactionId: number) => {
  if (downloadingFeeCsvIds.value.has(transactionId)) return;

  downloadingFeeCsvIds.value.add(transactionId);
  try {
    const response = await goSolidApi.get(
      `/api/money/admin/sales-transactions/${transactionId}/csv`,
      { responseType: "blob" }
    );
    downloadFromApiResponse(
      response.data,
      response.headers,
      `transaction_${transactionId}_fees.csv`,
      "text/csv"
    );
  } catch (error: unknown) {
    globalErrorHandler(error);
  } finally {
    downloadingFeeCsvIds.value.delete(transactionId);
  }
};

const fetchTransactions = async () => {
  isLoading.value = true;
  try {
    const response = await goSolidApi.get("/api/admin/sales-transactions");
    if (response.data) {
      transactions.value = response.data.sales_transactions;

      // Populate transactionInvoiceMap from fetched data
      transactionInvoiceMap.value = {};
      for (const transaction of transactions.value) {
        if (transaction.buyer_sales_invoice_id) {
          transactionInvoiceMap.value[transaction.id] =
            transaction.buyer_sales_invoice_id;
        }
      }
    } else {
      throw new Error("Error fetching transactions.");
    }
  } catch (error: unknown) {
    globalErrorHandler(error);
  } finally {
    isLoading.value = false;
  }
};

const reconcileBuyerInvoicePaidStatus = () =>
  runReconcile(async () => {
    try {
      const response = await goSolidApi.post(
        "/api/admin/sales-transactions/reconcile-buyer-invoice-paid-status"
      );
      if (response.data) {
        const updated = response.data.updated_count ?? 0;
        globalSuccessHandler(
          "Reconciliation complete",
          updated > 0
            ? `Marked ${updated} buyer invoice${updated === 1 ? "" : "s"} as paid.`
            : "No invoices needed updating."
        );
        await fetchTransactions();
      } else {
        throw new Error("Error reconciling buyer invoices.");
      }
    } catch (error: unknown) {
      globalErrorHandler(error);
    }
  });

const openConfirmationModal = (
  title: string,
  message: string,
  action: () => void
) => {
  confirmationModalTitle.value = title;
  confirmationModalMessage.value = message;
  confirmAction = action;
  showConfirmationModal.value = true;
};

const handleConfirm = () => {
  if (confirmAction) {
    confirmAction();
  }
  closeConfirmationModal();
};

const closeConfirmationModal = () => {
  showConfirmationModal.value = false;
  confirmationModalTitle.value = "";
  confirmationModalMessage.value = "";
  confirmAction = null;
};

const openTransferModal = (transactionId: number) => {
  selectedTransactionId.value = transactionId;
  transferringTransactionId.value = transactionId;
  showTransferModal.value = true;
};

const closeTransferModal = () => {
  showTransferModal.value = false;
  selectedTransactionId.value = 0;
  transferringTransactionId.value = null;
};

const handleTransferConfirm = async () => {
  // Transfer now handled by modal with polling
  // Modal will emit confirm when transfer is complete
  globalSuccessHandler(
    "Transfer Completed",
    "The volumes have been successfully transferred"
  );
  closeTransferModal();
  await fetchTransactions();
};

const completeTransaction = (transactionId: number) =>
  completing.run(transactionId, async () => {
    try {
      await goSolidApi.post(
        `/api/admin/transactions/${transactionId}/complete`
      );
      globalSuccessHandler(
        "Transaction Completed",
        "Fee invoice items have been generated"
      );
      await fetchTransactions();
    } catch (error: unknown) {
      globalErrorHandler(error);
    }
  });

const openCreateInvoiceModal = (transaction: SalesTransaction) => {
  selectedTransactionForInvoice.value = transaction;
  creatingInvoiceForTransactionId.value = transaction.id;
  showCreateInvoiceModal.value = true;
};

const handleModalClose = () => {
  showCreateInvoiceModal.value = false;
  selectedTransactionForInvoice.value = null;
  // Clear loading state when modal closes (in case user cancelled)
  // This is safe to call even if handleInvoiceCreated already cleared it
  creatingInvoiceForTransactionId.value = null;
};

const handleInvoiceCreated = async (invoiceId?: number) => {
  globalSuccessHandler(
    "Invoice Created",
    "Buyer sales invoice created successfully"
  );

  // Store invoice ID for this transaction
  if (invoiceId && selectedTransactionForInvoice.value) {
    transactionInvoiceMap.value[selectedTransactionForInvoice.value.id] =
      invoiceId;
  }

  // Close modal immediately so user doesn't have to wait for download
  showCreateInvoiceModal.value = false;
  selectedTransactionForInvoice.value = null;

  try {
    // Automatically download the invoice
    if (invoiceId) {
      await downloadInvoice(invoiceId);
    }
  } finally {
    // Clear loading state after download completes
    creatingInvoiceForTransactionId.value = null;
    // Re-fetch transactions after download completes
    await fetchTransactions();
  }
};

const handleSendInvoice = async (transaction: SalesTransaction) => {
  const invoiceId = transactionInvoiceMap.value[transaction.id];

  if (!invoiceId) {
    globalErrorHandler(
      new Error(
        "No invoice found for this transaction. Please create an invoice first."
      )
    );
    return;
  }

  if (!transaction.counterparty_emails) {
    globalErrorHandler(
      new Error("No email addresses available for this transaction")
    );
    return;
  }

  // Show confirmation modal
  openConfirmationModal(
    "Send Invoice",
    `Send invoice to ${transaction.counterparty_emails}?`,
    () =>
      sendingInvoice.run(transaction.id, async () => {
        try {
          await goSolidApi.post(
            `/api/admin/sales-transaction/${transaction.id}/send-buyer-invoice`
          );

          globalSuccessHandler("Success", "Invoice sent successfully");
          await fetchTransactions();
        } catch (error) {
          globalErrorHandler(error);
        }
      })
  );
};

const handleRegisterWithMerit = (transaction: SalesTransaction) => {
  openConfirmationModal(
    "Register with Merit",
    `Register the buyer sales invoice for ${transaction.counterparty_name} with Merit Aktiva? No emails will be sent.`,
    () =>
      registeringWithMerit.run(transaction.id, async () => {
        try {
          const response = await goSolidApi.post(
            `/api/admin/sales-transaction/${transaction.id}/register-buyer-invoice-with-merit`
          );
          globalSuccessHandler("Invoice registered", response.data.message);
          await fetchTransactions();
        } catch (error: unknown) {
          globalErrorHandler(error);
        }
      })
  );
};

const openMarkSentModal = (transaction: SalesTransaction) => {
  selectedTransactionForMarkSent.value = transaction;
  todayIsoDate.value = formatDateTimeIntoIsoDate(new Date());
  markSentDate.value = todayIsoDate.value;
  markSentForceToggle.value?.(true);
};

const closeMarkSentModal = () => {
  selectedTransactionForMarkSent.value = null;
  markSentDate.value = "";
  isMarkingSent.value = false;
};

const confirmMarkSent = async () => {
  const transaction = selectedTransactionForMarkSent.value;
  if (!transaction || !markSentDate.value) return;

  await runMarkSent(async () => {
    try {
      await goSolidApi.post(
        `/api/admin/sales-transaction/${transaction.id}/mark-buyer-invoice-sent`,
        { sent_on: markSentDate.value }
      );
      globalSuccessHandler("Success", "Invoice marked as sent");
      markSentForceToggle.value?.(false);
      closeMarkSentModal();
      await fetchTransactions();
    } catch (error) {
      globalErrorHandler(error);
    }
  });
};

const openDeliverModal = (transaction: SalesTransaction) => {
  selectedTransactionForDeliver.value = transaction;
  deliverTargetCompanyId.value = null;
  deliverForceToggle.value?.(true);
};

const closeDeliverModal = () => {
  selectedTransactionForDeliver.value = null;
  deliverTargetCompanyId.value = null;
  isDelivering.value = false;
};

const confirmDeliver = async () => {
  const transaction = selectedTransactionForDeliver.value;
  const targetCompanyId = deliverTargetCompanyId.value;
  if (!transaction || !targetCompanyId) return;

  await runDeliver(async () => {
    try {
      await goSolidApi.post("/api/admin/virtual-accounts/deliver-transaction", {
        sales_transaction_id: transaction.id,
        target_company_id: targetCompanyId,
      });

      globalSuccessHandler(
        "Delivery Complete",
        "Certificates have been delivered to the hosted account"
      );
      deliverForceToggle.value?.(false);
      closeDeliverModal();
      await fetchTransactions();
    } catch (error: unknown) {
      globalErrorHandler(error);
    }
  });
};

onMounted(fetchTransactions);
</script>

<style lang="scss" scoped>
#AdminTransactionsList {
  flex-grow: 1;
  text-align: left;
  overflow-y: auto;
  max-width: 100%;
}

.merit-todo-dot {
  position: absolute;
  top: -3px;
  right: -3px;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: var(--bs-danger);
}
</style>
